// =============================================================================
// MeuDriver — Página de relatório mensal do instrutor (I4)
// Rota: /instrutor/relatorio
// =============================================================================

import { useState } from 'react'
import { ChevronLeft, ChevronRight, FileBarChart } from 'lucide-react'
import { format, subMonths } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Button } from '@/components/ui/button'
import { useInstructorReport } from '@/hooks/useInstructorReport'
import { InstructorMonthlyReport } from '@/components/instructor/InstructorMonthlyReport'

// ---------------------------------------------------------------------------
// Gera lista dos últimos 12 meses (mais recente primeiro)
// ---------------------------------------------------------------------------

function buildMonthOptions(): { value: string; label: string }[] {
  const now = new Date()
  return Array.from({ length: 12 }, (_, i) => {
    const d = subMonths(now, i)
    const value = format(d, 'yyyy-MM')
    const label = format(d, "MMMM 'de' yyyy", { locale: ptBR })
    return { value, label: label.charAt(0).toUpperCase() + label.slice(1) }
  })
}

const MONTH_OPTIONS = buildMonthOptions()

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export default function InstructorReport() {
  const [monthIdx, setMonthIdx] = useState(0) // 0 = mês atual
  const currentOption = MONTH_OPTIONS[monthIdx]
  const { data, isLoading } = useInstructorReport(currentOption.value)

  function prev() {
    setMonthIdx((i) => Math.min(i + 1, MONTH_OPTIONS.length - 1))
  }
  function next() {
    setMonthIdx((i) => Math.max(i - 1, 0))
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileBarChart className="h-6 w-6 text-brand-600" />
            Relatório mensal
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Resumo de aulas, receita e avaliações do mês selecionado.
          </p>
        </div>
      </div>

      {/* Seletor de mês */}
      <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 w-fit">
        <Button
          variant="ghost"
          size="icon"
          onClick={prev}
          disabled={monthIdx >= MONTH_OPTIONS.length - 1}
          className="h-8 w-8"
          aria-label="Mês anterior"
          data-testid="report-prev-month"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <span className="min-w-[180px] text-center font-semibold text-gray-900 text-sm">
          {currentOption.label}
        </span>

        <Button
          variant="ghost"
          size="icon"
          onClick={next}
          disabled={monthIdx <= 0}
          className="h-8 w-8"
          aria-label="Próximo mês"
          data-testid="report-next-month"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Relatório */}
      <InstructorMonthlyReport
        data={data}
        isLoading={isLoading}
        month={currentOption.value}
      />
    </div>
  )
}
